/**
 * Updates the recently viewed products in localStorage.
 *
 * Two lists are kept. "viewedProducts" is the theme's own list (the last 4 products): the search drawer shows every id in it,
 * so it must stay short. "viewedProductsHistory" is a longer list (the last 12) for the Recently viewed row
 * (sections/recently-viewed.liquid). addProduct() updates both; the theme calls it on every product page from snippets/scripts.liquid.
 * Storage that is blocked or holds garbage never throws: reads give an empty list and writes are skipped.
 */

/** @param {string} key @returns {string[]} */
function readList(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

/** @param {string} key @param {string[]} list */
function writeList(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch (_) {
    // Storage is full or blocked: the row simply has nothing to show.
  }
}

export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key of the longer history used by the Recently viewed row */
  static #HISTORY_KEY = 'viewedProductsHistory';
  /** @static @constant {number} The maximum number of products to store for the search drawer */
  static #MAX_PRODUCTS = 4;
  /** @static @constant {number} The maximum number of products in the history */
  static #MAX_HISTORY = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = String(productId ?? '').trim();
    if (!id) return;

    const recent = [id, ...readList(this.#STORAGE_KEY).filter((/** @type {string} */ other) => other !== id)];
    writeList(this.#STORAGE_KEY, recent.slice(0, this.#MAX_PRODUCTS));

    const history = [id, ...readList(this.#HISTORY_KEY).filter((/** @type {string} */ other) => other !== id)];
    writeList(this.#HISTORY_KEY, history.slice(0, this.#MAX_HISTORY));
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
      localStorage.removeItem(this.#HISTORY_KEY);
    } catch (_) {
      // Nothing to clear when storage is blocked.
    }
  }

  /**
   * Retrieves the list of recently viewed products (the last 4, newest first).
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return readList(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the longer history (up to 12, newest first). Ids that are only in the short list (viewed before the history
   * existed) are added at the end.
   * @returns {string[]} The list of viewed products.
   */
  static getHistory() {
    const history = readList(this.#HISTORY_KEY);
    for (const id of readList(this.#STORAGE_KEY)) {
      if (!history.includes(id)) history.push(id);
    }
    return history.slice(0, this.#MAX_HISTORY);
  }
}
